/**
 * useBulkCreateConsentData Hook
 *
 * Custom hook for fetching bulk create consent MIS dashboard data
 * Uses bulk_action = 'create_consent' to fetch only consent creation batches
 */

import { useState, useEffect, useCallback } from 'react';
import { bulkMisAPI } from '../../../../services/bulk-mis-api.service';
import { useErrorHandler } from '../../../../hooks/useErrorHandler';
import type {
  BulkMisAPIResponse,
  BulkMisFilters,
  GetBulkBatchesParams,
} from '../../../../types/bulk-mis';

interface UseBulkCreateConsentDataReturn {
  data: BulkMisAPIResponse['data'] | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useBulkCreateConsentData(
  filters: BulkMisFilters,
  page: number,
  pageSize: number
): UseBulkCreateConsentDataReturn {
  const [data, setData] = useState<BulkMisAPIResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const { handleError, errorMessage, clearError } = useErrorHandler();

  const fetchData = useCallback(async () => {
    setLoading(true);
    clearError();

    try {
      const params: GetBulkBatchesParams = {
        page,
        limit: pageSize,
        sort_by: 'created_at',
        sort_order: 'desc',
        bulk_action: 'consent_request', // Key difference: filter for consent_request batches (bulk create consent)
      };

      // Add filters if set
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      if (filters.pipelineSource && filters.pipelineSource !== 'all') {
        params.pipeline_source = filters.pipelineSource;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.dateRange.from) {
        params.from_date = filters.dateRange.from.toISOString();
      }

      if (filters.dateRange.to) {
        params.to_date = filters.dateRange.to.toISOString();
      }

      const response = await bulkMisAPI.getBatches(params);

      setData(response.data);
    } catch (err) {
      console.error('[useBulkCreateConsentData] Error fetching data:', err);
      handleError(err, { component: 'useBulkCreateConsentData', action: 'fetchData' });
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, handleError, clearError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error: errorMessage,
    refresh: fetchData,
  };
}
